// -----------------------------------------------------------------------------
// SidebarRedirectConfirmation
//
// Compact confirmation view shown after a sidebar composer tab has been opened.
// The user sends their message in the right-sidebar, adds optional notes here,
// then clicks the primary button to POST the note + mark the action complete.
//
// Note POST: mirrors LogCallPanel — POST /leads/{id}/notes/ with
//   contacted_via = EMAIL | WHATSAPP (derived from channel).
//   Description = notes text if provided, else a channel-appropriate fallback.
//   Skips POST gracefully if practiceId or leadId are absent.
//
// Used by:
//   FollowUpPanel   — Email/WhatsApp channels (body/doneLabel for follow-up copy)
//   ActionPanel     — EMAIL_ACTION_TYPES (body/doneLabel for dedicated email copy)
//
// Props:
//   channel      — 'email' | 'whatsapp'  drives icon + accent colour + contacted_via
//   body         — explanatory paragraph shown below the heading
//   doneLabel    — label for the green primary button
//   onComplete   — called after successful note POST (or immediately if no IDs)
//   onBack       — optional; if provided renders a "← Back" secondary button
//   practiceId   — required for note POST
//   leadId       — required for note POST
//   leadDetail   — used for treatment ID on POST
//   noSidebar    — ADDITIVE, default false. When true (e.g. the full-screen
//                  Completed-Treatment hub, which has NO right sidebar) the
//                  title + body are overridden with honest copy that points the
//                  user to the lead's detail tabs instead of "the sidebar".
//                  When false/absent the rendered text is byte-for-byte
//                  unchanged from the attended flow.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, Stack, Button, TextField, CircularProgress } from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';

const CHANNEL_META = {
    email: {
        icon: 'mdi:email-outline',
        color: '#078DEE',
        bg: 'rgba(7,141,238,0.08)',
        title: 'Email composer is open',
        contactedVia: 'EMAIL',
        notesFallback: 'Sent email to patient',
        notesPlaceholder: 'Any notes about this email? (optional)',
    },
    whatsapp: {
        icon: 'ic:baseline-whatsapp',
        color: '#25D366',
        bg: 'rgba(37,211,102,0.08)',
        title: 'WhatsApp composer is open',
        contactedVia: 'WHATSAPP',
        notesFallback: 'Sent WhatsApp message to patient',
        notesPlaceholder: 'Any notes about this message? (optional)',
    },
};

// Sidebarless overrides for the full-screen hub (no right sidebar exists there).
// Keyed by channel; only used when the additive `noSidebar` prop is true.
const NO_SIDEBAR_META = {
    email: {
        title: 'Send the email',
        body: "Open the Email tab from the lead's detail to send, then mark this done.",
    },
    whatsapp: {
        title: 'Send the WhatsApp',
        body: "Open the WhatsApp tab from the lead's detail to send, then mark this done.",
    },
};

export default function SidebarRedirectConfirmation({
    channel,
    body,
    doneLabel,
    onComplete,
    onBack,
    practiceId,
    leadId,
    leadDetail,
    noSidebar,
}) {
    const baseMeta = CHANNEL_META[channel] || CHANNEL_META.email;
    // When noSidebar is set, override the title and the body with honest,
    // sidebar-free copy. Default path leaves both exactly as before.
    const noSidebarMeta = NO_SIDEBAR_META[channel] || NO_SIDEBAR_META.email;
    const meta = noSidebar
        ? { ...baseMeta, title: noSidebarMeta.title }
        : baseMeta;
    const resolvedBody = noSidebar ? noSidebarMeta.body : body;
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            // POST contact note if we have the required IDs.
            // Note is a nice-to-have: surface error toast on failure but always
            // proceed to onComplete so the checklist item is marked done.
            if (practiceId && leadId) {
                const treatmentId = leadDetail?.lead_treatments?.[0]?.id || null;
                // The LeadNote API stores free text in `additional_notes` (NOT `description`,
                // which the serializer silently ignores). Sending `description` was dropping
                // the consultant's typed note. See apps/leads/serializers.py LeadNoteSerializer.
                const additionalNotes = notes.trim() || meta.notesFallback;
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/notes/`,
                    'post',
                    {
                        lead: leadId,
                        additional_notes: additionalNotes,
                        contacted_via: meta.contactedVia,
                        ...(treatmentId ? { treatment: treatmentId } : {}),
                    }
                );
                if (res.status !== 201) {
                    const msg = res?.data?.detail || res?.data?.additional_notes?.[0] || 'Failed to log note';
                    dispatch(updateError({ success: false, message: msg }));
                }
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
        }
        // Always mark the checklist item done — note POST is a nice-to-have.
        onComplete();
    };

    return (
        <Box
            data-testid="sidebar-redirect-confirmation"
            sx={{
                width: '100%',
                minWidth: 0,
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                px: 2.5,
                py: 2.5,
                gap: 2,
                alignItems: 'center',
                textAlign: 'center',
            }}
        >
            {/* Channel icon */}
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '14px',
                    bgcolor: meta.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Iconify icon={meta.icon} width={26} height={26} sx={{ color: meta.color }} />
            </Box>

            {/* Title + body */}
            <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    {meta.title}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '13px',
                        color: '#6e6e73',
                        mt: 0.5,
                        letterSpacing: '-0.005em',
                        px: 1,
                        lineHeight: 1.5,
                    }}
                >
                    {resolvedBody}
                </Typography>
            </Box>

            {/* Notes textarea — optional, full-width */}
            <Box sx={{ width: '100%', textAlign: 'left' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.75 }}>
                    Notes
                </Typography>
                <TextField
                    data-testid="sidebar-confirm-notes"
                    multiline
                    minRows={2}
                    maxRows={4}
                    fullWidth
                    size="small"
                    placeholder={meta.notesPlaceholder}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={submitting}
                />
            </Box>

            {/* Action buttons */}
            <Stack spacing={1} sx={{ width: '100%' }}>
                <Button
                    data-testid="sidebar-confirm-done-btn"
                    variant="contained"
                    fullWidth
                    onClick={handleConfirm}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                    sx={{
                        bgcolor: '#00A76F',
                        color: '#fff',
                        textTransform: 'none',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#007867' },
                        '&.Mui-disabled': { bgcolor: '#00A76F88', color: '#fff' },
                    }}
                >
                    {submitting ? 'Saving…' : doneLabel}
                </Button>

                {onBack && (
                    <Button
                        data-testid="sidebar-confirm-back-btn"
                        variant="text"
                        fullWidth
                        disabled={submitting}
                        startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={14} height={14} />}
                        onClick={onBack}
                        sx={{
                            color: '#078DEE',
                            textTransform: 'none',
                            fontSize: '13px',
                            fontWeight: 500,
                            '&:hover': { bgcolor: 'rgba(7,141,238,0.06)' },
                        }}
                    >
                        Back
                    </Button>
                )}
            </Stack>
        </Box>
    );
}

SidebarRedirectConfirmation.propTypes = {
    channel: PropTypes.oneOf(['email', 'whatsapp']).isRequired,
    body: PropTypes.string.isRequired,
    doneLabel: PropTypes.string.isRequired,
    onComplete: PropTypes.func.isRequired,
    onBack: PropTypes.func,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    /** Additive, default false. When true, overrides title+body with sidebar-free copy. */
    noSidebar: PropTypes.bool,
};

SidebarRedirectConfirmation.defaultProps = {
    onBack: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    noSidebar: false,
};
